import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, shareReplay, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';

export interface ChatOption {
  targetNodeId: string;
}

export interface ChatRequest {
  nodeId: string;
}

export interface ChatResponse {
  message: string;
  options: ChatOption[];
  currentNodeId: string;
  isDynamic: boolean;
}

export interface ConversationNode {
  id: string;
  message: string;
  options: ChatOption[];
  isDynamic?: boolean;
}

interface ConversationTree {
  nodes: ConversationNode[];
}

@Injectable({
  providedIn: 'root',
})
export class LocalChatService {
  private readonly treeUrl = './assets/faq/conversation-tree.json';

  private readonly tree$: Observable<ConversationTree> = this.http
    .get<ConversationTree>(this.treeUrl)
    .pipe(shareReplay(1));

  constructor(private readonly http: HttpClient) {}

  getStartNode(): Observable<ChatResponse> {
    return this.getNode('root');
  }

  selectOption(request: ChatRequest): Observable<ChatResponse> {
    return this.getNode(request.nodeId);
  }

  private getNode(nodeId: string): Observable<ChatResponse> {
    return this.tree$.pipe(
      switchMap((tree) => {
        const node = tree.nodes.find((n) => n.id === nodeId);
        if (!node) {
          return throwError(() => new Error(`Node '${nodeId}' not found.`));
        }
        return [node];
      }),
      map((node) => this.toResponse(node))
    );
  }

  private toResponse(node: ConversationNode): ChatResponse {
    return {
      message: node.message,
      options: node.options,
      currentNodeId: node.id,
      isDynamic: node.isDynamic ?? false,
    };
  }
}
